import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOUR_STEPS, TourStep } from './tourSteps';

interface Props {
  agentEnabled: boolean;
  onClose: () => void;
}

type Rect = { top: number; left: number; width: number; height: number };

const PAD = 8;
const CARD_W = 360;

/** The visible element for a step's target, if it's on screen right now. */
function findTarget(step: TourStep): HTMLElement | null {
  if (!step.target) return null;
  const el = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? el : null;
}

const ProductTour: React.FC<Props> = ({ agentEnabled, onClose }) => {
  const steps = useMemo(() => TOUR_STEPS.filter((s) => !s.needsAgent || agentEnabled), [agentEnabled]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = steps[index];
  const last = index === steps.length - 1;

  const measure = useCallback(() => {
    const el = findTarget(step);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
  }, [step]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const next = useCallback(() => (last ? onClose() : setIndex((i) => i + 1)), [last, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, back, onClose]);

  // Card placement: below the spotlight when there's room, otherwise above;
  // horizontally clamped to the viewport. No target → centered.
  const cardStyle: React.CSSProperties = (() => {
    if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: `min(${CARD_W + 80}px, calc(100vw - 32px))` };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(16, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 16);
    const below = rect.top + rect.height + 12;
    const fitsBelow = below + 240 < vh;
    return fitsBelow
      ? { top: below, left, width: CARD_W }
      : { bottom: Math.max(16, vh - rect.top + 12), left, width: CARD_W };
  })();

  return createPortal(
    <div className="fixed inset-0 z-[1000]" role="dialog" aria-modal="true" aria-labelledby="product-tour-title">
      {rect ? (
        // Spotlight: a transparent box whose huge shadow dims everything else.
        <div
          className="fixed rounded-lg pointer-events-none transition-all duration-200 ring-2 ring-primary"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height, boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.6)' }}
        />
      ) : (
        <div className="fixed inset-0 bg-slate-900/60" />
      )}
      {/* Clicks outside the card do nothing (the tour is dismissed with Skip/Esc). */}
      <div className="fixed inset-0" />

      <div
        className="fixed bg-white dark:bg-dark-surface text-gray-900 dark:text-gray-100 rounded-xl shadow-2xl border border-gray-200 dark:border-dark-border p-5"
        style={cardStyle}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-primary">
            {index + 1} of {steps.length}
          </span>
          <button type="button" onClick={onClose} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            Skip tour
          </button>
        </div>
        <h2 id="product-tour-title" className="text-lg font-semibold mb-2">
          {step.title}
        </h2>
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {step.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <div className="flex items-center justify-between mt-5">
          <div className="flex gap-1" aria-hidden="true">
            {steps.map((s, i) => (
              <span key={s.id} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-primary' : 'w-1.5 bg-gray-300 dark:bg-gray-600'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {index > 0 && (
              <button
                type="button"
                onClick={back}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-border"
              >
                Back
              </button>
            )}
            <button type="button" onClick={next} autoFocus className="px-4 py-1.5 text-sm rounded-lg bg-primary text-white hover:bg-blue-600">
              {last ? 'Done' : index === 0 ? 'Show me' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductTour;
